import React, { createContext, ReactNode, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DialogReset } from '@futrli/ui-components/components/dialog-wizard/dialog-reset/dialog-reset';
import { DuplicateDashboardSidebar } from '@futrli/predict-next/forecasting/dashboard/sidebars/duplicate-dashboard-sidebar/duplicate-dashboard-sidebar';
import { ExtendedLiveReportList } from '@futrli/views/processors/dashboards/dashboardsList';
import { getReportingEditReportUrl, matchConsolidationDetailRoutePath, useModal } from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { AddToPerformanceAreaDialog } from '../../dialogs/add-to-performance-area-dialog/add-to-performance-area-dialog';
import { DeleteDashboardDialog } from '../../dialogs/delete-dashboard-dialog/delete-dashboard-dialog';
import { PublishDashboardDialog } from '../../dialogs/publish-dashboard-dialog/publish-dashboard-dialog';
import { RemoveFromPerformanceAreaDialog } from '../../dialogs/remove-from-performance-area-dialog/remove-from-performance-area-dialog';

interface DashboardActionsContextValue {
  handleAddToPerformanceAreaClick: (dashboard: ExtendedLiveReportList) => void;
  handleAddToTemplatesClick: (dashboard: ExtendedLiveReportList) => void;
  handleDeleteClick: (dashboard: ExtendedLiveReportList) => void;
  handleDuplicateClick: (dashboard: ExtendedLiveReportList) => void;
  handleEditClick: (dashboard: ExtendedLiveReportList) => void;
  handleNameClick: (dashboard: ExtendedLiveReportList) => void;
  handlePublishClick: (dashboard: ExtendedLiveReportList) => void;
  handleRemoveFromPerformanceAreaClick: (dashboard: ExtendedLiveReportList) => void;
  handleShareClick: (dashboard: ExtendedLiveReportList) => void;
  handleUnpublishClick: (dashboard: ExtendedLiveReportList) => void;
}

const DashboardActionsContext = createContext<DashboardActionsContextValue>(
  ({} as unknown) as DashboardActionsContextValue
);

export const DashboardActionsProvider = ({ children }: { children: ReactNode }) => {
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);
  const navigate = useNavigate();

  const addToPerformanceAreaDialog = useModal();
  const deleteDialog = useModal();
  const publishDialog = useModal();
  const removeFromPerformanceAreaDialog = useModal();
  const duplicateSidebar = useModal();

  const { dashboardTemplate, shareResource } = useSidebarContext();

  const [dashboard, setDashboard] = React.useState<ExtendedLiveReportList>();

  const handleAddToPerformanceAreaClick = useCallback(
    (_dashboard: ExtendedLiveReportList) => {
      setDashboard(_dashboard);
      addToPerformanceAreaDialog.open();
    },
    [addToPerformanceAreaDialog]
  );

  const handleAddToTemplatesClick = useCallback(
    (_dashboard: ExtendedLiveReportList) => {
      dashboardTemplate.open({ reportId: _dashboard.id });
    },
    [dashboardTemplate]
  );

  const handleDeleteClick = useCallback(
    (dashboard: ExtendedLiveReportList) => {
      setDashboard(dashboard);
      deleteDialog.open();
    },
    [deleteDialog]
  );

  const handleDuplicateClick = useCallback(
    (_dashboard: ExtendedLiveReportList) => {
      setDashboard(_dashboard);
      duplicateSidebar.open();
    },
    [duplicateSidebar]
  );

  const handleEditClick = useCallback(
    (_dashboard: ExtendedLiveReportList) => {
      navigate(getReportingEditReportUrl(workspaceId, organisationId, _dashboard.id, isConsolidation));
    },
    [isConsolidation, navigate, organisationId, workspaceId]
  );

  const handleNameClick = useCallback(
    (_dashboard: ExtendedLiveReportList) => {
      navigate(getReportingEditReportUrl(workspaceId, organisationId, _dashboard.id, isConsolidation));
    },
    [isConsolidation, navigate, organisationId, workspaceId]
  );

  const handlePublishClick = useCallback(
    (dashboard: ExtendedLiveReportList) => {
      setDashboard(dashboard);
      publishDialog.open();
    },
    [publishDialog]
  );

  const handleRemoveFromPerformanceAreaClick = useCallback(
    (_dashboard: ExtendedLiveReportList) => {
      setDashboard(_dashboard);
      removeFromPerformanceAreaDialog.open();
    },
    [removeFromPerformanceAreaDialog]
  );

  const handleUnpublishClick = useCallback(
    (dashboard: ExtendedLiveReportList) => {
      setDashboard(dashboard);
      publishDialog.open();
    },
    [publishDialog]
  );

  const handleShareClick = useCallback(
    (dashboard: ExtendedLiveReportList) => {
      shareResource.open({
        entityId: dashboard.id,
        entityType: 'live-report',
        resourceName: dashboard.name
      });
    },
    [shareResource]
  );

  const value = {
    handleAddToPerformanceAreaClick,
    handleAddToTemplatesClick,
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handleNameClick,
    handlePublishClick,
    handleRemoveFromPerformanceAreaClick,
    handleShareClick,
    handleUnpublishClick
  };

  return (
    <DashboardActionsContext.Provider value={value}>
      {dashboard?.id && (
        <>
          <DialogReset resetOn={addToPerformanceAreaDialog.isOpen}>
            <AddToPerformanceAreaDialog
              dashboardId={dashboard?.id}
              isOpen={addToPerformanceAreaDialog.isOpen}
              onClose={addToPerformanceAreaDialog.close}
            />
          </DialogReset>
          <DialogReset resetOn={deleteDialog.isOpen}>
            <DeleteDashboardDialog
              dashboardId={dashboard?.id}
              isOpen={deleteDialog.isOpen}
              onClose={deleteDialog.close}
            />
          </DialogReset>
          <DialogReset resetOn={publishDialog.isOpen}>
            <PublishDashboardDialog
              dashboardId={dashboard?.id}
              isOpen={publishDialog.isOpen}
              onClose={publishDialog.close}
            />
          </DialogReset>
          <DialogReset resetOn={removeFromPerformanceAreaDialog.isOpen}>
            <RemoveFromPerformanceAreaDialog
              dashboardId={dashboard?.id}
              isOpen={removeFromPerformanceAreaDialog.isOpen}
              onClose={removeFromPerformanceAreaDialog.close}
            />
          </DialogReset>
          <DuplicateDashboardSidebar
            dashboardId={dashboard?.id}
            isOpen={duplicateSidebar.isOpen}
            onClose={duplicateSidebar.close}
          />
        </>
      )}
      {children}
    </DashboardActionsContext.Provider>
  );
};

export const useDashboardActions = () => {
  const context = useContext(DashboardActionsContext);
  if (!context) {
    throw new Error('useDashboardActions must be used within a DashboardActionsProvider');
  }
  return context;
};
